export default class IndexManager {

  constructor(widgetId, params) {
    this.key = h.args('key');
    this.hash = h.args('hash');

    this.widgetId = widgetId;
    this.availableSettingParams = Object.assign({
      // Global Params
      'preview': false,
      'static': false,
      'debug': false,
      'use_placeholder': false,
    }, params);

    this.themeSetting = {};
    this.themeProps = {};

    this.widget = null;
    this.containerElement = document.body;
    this._isOnModeration = null;
  }

  // Get theme setting, should be called after loadSettings
  get(index, value = null) {
    return this.themeSetting[index] || value;
  }

  // Setup should be called after loadSettings and loadAssets
  async setupWidget(widget) {
    this.widget = widget;
    await this.widget.setup(this.themeSetting, this.themeProps);
  }

  apiUrl(endpoint = '') {
    return (h.args('sbx_api_url') || 'https://api.trakteer.id') + `/v2/${endpoint}`;
  }

  async loadSettings() {
    Object.entries(this.availableSettingParams).forEach(settingParams => {
      this.themeSetting[settingParams[0]] = h.args(settingParams[0]) || settingParams[1];
    });

    return this.themeSetting;
  }

  async loadAssets() {
    this.themeProps = await this.loadThemes();

    // Setup loaded themes
    for (const theme in this.themeProps) {
      this.loadHTML(this.themeProps[theme]);
      await this.loadFonts(this.themeProps[theme]);
      await this.loadExtraDependencies(this.themeProps[theme]);
    }
  }

  async loadThemes() {
    const theme = this.themeSetting[`${this.widgetId}_theme`];

    const jsThemeHash = await h.hashmap(`${h.dirPath()}/theme-${theme}.js`)
    await h.addHeadScript(`theme-${theme}.js?${jsThemeHash}`)
    const cssThemeHash = await h.hashmap(`${h.dirPath()}/theme-${theme}.css`)
    await h.addHeadStyle(`theme-${theme}.css?${cssThemeHash}`)

    return h.prop(window, `tr.${this.widgetId}`);
  } 

  loadHTML(theme) {
    const html = theme.htmlContainer().trim();
    this.containerElement.insertAdjacentHTML('beforeend', html);
  }

  async loadFonts(theme) {
    if(this.widgetId == 'po') {
      const fontTitle = this.themeSetting[`${this.widgetId}_font_title`] || theme.font().default;
      const fontContent = this.themeSetting[`${this.widgetId}_font_content`] || theme.font().default;
      WebFont.load({
        google: {
          families: [
            fontTitle.split(' ').join('+'),
            fontContent.split(' ').join('+'),
          ]
        }
      });

    } else {
      const font = this.themeSetting[`${this.widgetId}_font`] || theme.font().default;
      WebFont.load({
        google: {
          families: [
            font.split(' ').join('+')
          ]
        }
      });
    }

  }

  async loadExtraDependencies(theme) {
    const deps = theme.dependencies();
    for (const dep in deps) {
      for (let i = 0; i < deps[dep].length; i++) {
        if (dep === 'js') {
          await h.addHeadScript(deps[dep][i]);
        }
        else if (dep === 'css') {
          await h.addHeadStyle(deps[dep][i]);
        }
      }
    }
  }

  async loadData() {
    // Shoulde overrides on the caller
  }

  async getModerationMode() {
    // const res = await superagent.get(h.apiUrl(`tip/${this.key}/moderation`)).accept('application/json');
    // return res.body.isOnModeration;
    try {
      const res = await superagent
        .get(h.apiUrl(`tip/${this.key}/moderation`))
        .accept('application/json');
        
      return res.body.isOnModeration;
    } catch (err) {
      return this._isOnModeration;
    }
  }

  async init(params) {

    // Load Settings
    await this.loadSettings();

    // Load Moderation
    this._isOnModeration = await this.getModerationMode();
    this.listenerModerationStatus();

    // Load Assets
    await this.loadAssets();

    // Load Widget
    const widget = params.widget || null;
    await this.setupWidget(widget);

    // Load Preview and Data
    const isPreview = h.isTrue(this.themeSetting['preview']);
    if (isPreview) { 
      document.domain = h.rootDomain(window.location.origin); // Allow same origin

      const handlePreview = params.preview || null
      if (handlePreview) {
        await handlePreview(this);
      }
    } else {
      if (typeof window.StreamVersionStatus === 'undefined') {
        await h.addHeadScript(h.widgetUrl('utility/stream-version-status.js'));
      }
      window.StreamVersionStatus.init({ mode: 'widget', key: this.key });
      await this.loadData();
    }

    // Load Debug Tools
    if (h.isTrue(this.themeSetting['debug'])) {
      const debuggingProps = params.debug || null;
      await this.handleDebug(debuggingProps);
    }

    // listener for form custom theme and handle update milestone list
    if(isPreview){
      this.handleUpdatePreview();
    }
  }

  addListenerOn(type, fn) {
    if (type === 'new-tip') {
      let self = this;
      window.Echo.channel('creator-stream.' + this.hash + '.' + this.key).notification(async function (data) {
        const shouldSkipModeration = await self.getShouldSkipModeration(data)
        if (data.type === 'new-tip-success') {
          if(self._isOnModeration && !shouldSkipModeration) {
            //don't process new tip
          } else {
            //Process new tip
            fn(data);
          }
        } else if(data.type === 'new-tip-success-approved') {
          if(self._isOnModeration && !shouldSkipModeration) {
            //Process tip approved
            fn(data);
          } else {
            //don't process tip approved
          }
        } else if(data.type === 'new-tip-replay') {
          // Process tip replay
          fn(data);
        }
      });
    }

    if (type === 'new-tip-simulation') {
      window.Echo.channel('creator-stream-test.' + this.hash + '.' + this.key).notification(function (data) {
        if (data.type === 'new-tip-simulation') {
          fn(data);
        }
      });
    }
  }

  listenerModerationStatus() {
    let self = this;
    window.Echo.channel('creator-moderation-status.' + this.hash + '.' + this.key).notification(function (data) {
      if(data.type === 'moderation-status') {
        self._isOnModeration = (data.moderation_status == 1) ? true : false;
        console.log("Moderation status has been updated!", self._isOnModeration)
      }
    });
  }

  async getShouldSkipModeration(data) {
    let _isShowingSupportMessage = false;
    if(typeof this.getShowMessage  === "function") {
      _isShowingSupportMessage = await this.getShowMessage()
    }
    if (data.supporter_message && _isShowingSupportMessage) { // check if data have message and check setting widget have setting show message or not
      return false; // must check in moderation 
    } else {
      return true; // just skip moderation
    }
  }

  async handleDebug(props) {
    const debugbarJsThemeHash = await h.hashmap(`utility/debugbar.js`);
    await h.addHeadScript(`../utility/debugbar.js?${debugbarJsThemeHash}`);

    if (window['debugbar']) {
      window['debugbar'].load(this.themeSetting, props, this);
    }

    const debugsceneJsThemeHash = await h.hashmap(`utility/debugscene.js`);
    await h.addHeadScript(`../utility/debugscene.js?${debugsceneJsThemeHash}`);

    if (window['debugscene']) {
      await window['debugscene'].load();
    }
  }
  handleUpdatePreview(){
    if(this.themeSetting[`${this.widgetId}_theme`] == 'custom') {
      window['tempWidgetTheme'] = this.widget.theme;
      if (typeof this.widget.theme.updatePreview === 'function') {
        // Function exists
        window.addEventListener('message', (e) => {
          if ('html' in e.data) {
            this.widget.theme.updatePreview(e);
          }
        });
      }
    }
  }

}
